import { MotorbikeDetail } from 'src/redux/motorbikesReducer'

export interface MobilePromotionFormData {
  brand_id: string
  model: string
  category: string
  capacity: string
  engine_power: string
  drive_type: string
  gear_box: string
  color: string
  metallic: string
  price: string
  vat_rate: string
  damage_unrepaired: string
  abs: string
  heated_grips: string
  box: string
  windshield: string
  kickstarter: string
  first_registration: string
  total_mileage: string
  vin: string
  number_of_previous_owners: string
  last_inspection: string
  new_inspection: string
  full_service_history: string
  description: string
  highlight_1: string
  highlight_2: string
  highlight_3: string
}

const PRICE_PATTERN = /^\d+(?:\.\d{1,2})?$/
const INTEGER_PATTERN = /^\d+$/
const MONTH_PATTERN = /^\d{4}-(?:0[1-9]|1[0-2])$/

const textValue = (value: string | number | null): string => {
  if (value === null) {
    return ''
  }
  return String(value)
}

const monthValue = (value: string | null): string => {
  if (value === null || value.length < 7) {
    return ''
  }
  return value.slice(0, 7)
}

export const emptyMobilePromotionForm = (): MobilePromotionFormData => ({
  brand_id: '',
  model: '',
  category: '',
  capacity: '',
  engine_power: '',
  drive_type: '',
  gear_box: '',
  color: '',
  metallic: '',
  price: '',
  vat_rate: '',
  damage_unrepaired: '',
  abs: '',
  heated_grips: '',
  box: '',
  windshield: '',
  kickstarter: '',
  first_registration: '',
  total_mileage: '',
  vin: '',
  number_of_previous_owners: '',
  last_inspection: '',
  new_inspection: '',
  full_service_history: '',
  description: '',
  highlight_1: '',
  highlight_2: '',
  highlight_3: ''
})

export interface MobilePromotionFormSource {
  brand_id: number | null
  model: string | null
  category: string | null
  capacity: number | null
  engine_power: number | null
  drive_type: string | null
  gear_box: string | null
  color: string | null
  metallic: boolean | null
  price: number | null
  vat_rate: string | null
  damage_unrepaired: boolean | null
  abs: boolean | null
  heated_grips: boolean | null
  box: boolean | null
  windshield: boolean | null
  kickstarter: boolean | null
  first_registration: string | null
  total_mileage: number | null
  vin: string | null
  number_of_previous_owners: number | null
  last_inspection: string | null
  new_inspection: boolean | null
  full_service_history: boolean | null
  description: string | null
  highlights: string[]
}

const boolValue = (value: boolean | null | undefined): string => {
  if (value === true) {
    return 'true'
  }
  if (value === false) {
    return 'false'
  }
  return ''
}

export const mobilePromotionFormFromPromotion = (promotion: MobilePromotionFormSource): MobilePromotionFormData => ({
  ...emptyMobilePromotionForm(),
  brand_id: promotion.brand_id === null ? '' : String(promotion.brand_id),
  model: textValue(promotion.model),
  category: textValue(promotion.category),
  capacity: textValue(promotion.capacity),
  engine_power: textValue(promotion.engine_power),
  drive_type: textValue(promotion.drive_type),
  gear_box: textValue(promotion.gear_box),
  color: textValue(promotion.color),
  metallic: boolValue(promotion.metallic),
  price: textValue(promotion.price),
  vat_rate: textValue(promotion.vat_rate),
  damage_unrepaired: boolValue(promotion.damage_unrepaired),
  abs: boolValue(promotion.abs),
  heated_grips: boolValue(promotion.heated_grips),
  box: boolValue(promotion.box),
  windshield: boolValue(promotion.windshield),
  kickstarter: boolValue(promotion.kickstarter),
  first_registration: monthValue(promotion.first_registration),
  total_mileage: textValue(promotion.total_mileage),
  vin: textValue(promotion.vin),
  number_of_previous_owners: textValue(promotion.number_of_previous_owners),
  last_inspection: monthValue(promotion.last_inspection),
  new_inspection: boolValue(promotion.new_inspection),
  full_service_history: boolValue(promotion.full_service_history),
  description: textValue(promotion.description),
  highlight_1: promotion.highlights[0] ?? '',
  highlight_2: promotion.highlights[1] ?? '',
  highlight_3: promotion.highlights[2] ?? ''
})

export const mobilePromotionFormFromMotorbike = (motorbike: MotorbikeDetail): MobilePromotionFormData => ({
  ...emptyMobilePromotionForm(),
  brand_id: String(motorbike.brand_id),
  model: motorbike.model,
  capacity: textValue(motorbike.capacity),
  engine_power: textValue(motorbike.engine_power),
  first_registration: monthValue(motorbike.first_registration),
  total_mileage: textValue(motorbike.total_mileage),
  vin: textValue(motorbike.vin),
  number_of_previous_owners: textValue(motorbike.number_of_previous_owners),
  metallic: boolValue(motorbike.metallic),
  drive_type: textValue(motorbike.drive_type),
  gear_box: textValue(motorbike.gear_box),
  damage_unrepaired: boolValue(motorbike.damage_unrepaired),
  abs: boolValue(motorbike.abs),
  heated_grips: boolValue(motorbike.heated_grips),
  box: boolValue(motorbike.box),
  windshield: boolValue(motorbike.windshield),
  last_inspection: monthValue(motorbike.next_hu),
  full_service_history: boolValue(motorbike.full_service_history)
})

const fieldText = (value: string | null | undefined): string => {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim()
}

const optionalInteger = (
  value: string | null | undefined,
  errors: Partial<MobilePromotionFormData>,
  field: keyof MobilePromotionFormData
): void => {
  const trimmed = fieldText(value)
  if (trimmed !== '' && !INTEGER_PATTERN.test(trimmed)) {
    errors[field] = 'required'
  }
}

const optionalMonth = (
  value: string | null | undefined,
  errors: Partial<MobilePromotionFormData>,
  field: keyof MobilePromotionFormData
): void => {
  const trimmed = fieldText(value)
  if (trimmed !== '' && !MONTH_PATTERN.test(trimmed)) {
    errors[field] = 'required'
  }
}

export const validateMobilePromotionForm = (values: MobilePromotionFormData): Partial<MobilePromotionFormData> => {
  const errors: Partial<MobilePromotionFormData> = {}
  const price = fieldText(values.price)
  if (!PRICE_PATTERN.test(price) || Number(price) <= 0) {
    errors.price = 'required'
  }
  if (fieldText(values.category) === '') {
    errors.category = 'required'
  }
  if (fieldText(values.brand_id) === '') {
    errors.brand_id = 'required'
  }
  if (fieldText(values.model) === '') {
    errors.model = 'required'
  }
  if (fieldText(values.vat_rate) === '') {
    errors.vat_rate = 'required'
  }
  if (values.damage_unrepaired !== 'true' && values.damage_unrepaired !== 'false') {
    errors.damage_unrepaired = 'required'
  }
  if (!MONTH_PATTERN.test(fieldText(values.first_registration))) {
    errors.first_registration = 'required'
  }
  if (!INTEGER_PATTERN.test(fieldText(values.total_mileage))) {
    errors.total_mileage = 'required'
  }
  const vin = fieldText(values.vin)
  if (vin !== '' && vin.length !== 17) {
    errors.vin = 'required'
  }
  optionalInteger(values.capacity, errors, 'capacity')
  optionalInteger(values.engine_power, errors, 'engine_power')
  optionalInteger(values.number_of_previous_owners, errors, 'number_of_previous_owners')
  optionalMonth(values.last_inspection, errors, 'last_inspection')
  return errors
}

export const mobilePromotionHighlights = (values: MobilePromotionFormData): string[] => {
  return [values.highlight_1, values.highlight_2, values.highlight_3].filter((highlight) => highlight.trim() !== '')
}
